import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { fetchWithToken } from "../api/apiutils";

const ENTITY_OPTIONS = [
  "Announce",
  "Contract",
  "Lot",
  "Plan",
  "Rnu",
  "Supplier",
  "TrdHelper",
];

// Собирает ISO-строку из локального времени
const toUtcIso = (value) => {
  if (!value) return "";
  return new Date(value).toISOString();
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const STATE_META = {
  0: { label: "Выполняется", color: "info" },
  1: { label: "Завершено", color: "success" },
  2: { label: "Ошибка", color: "error" },
  3: { label: "Отменен", color: "warning" },
};

const getStateMeta = (state) =>
  STATE_META[state] ?? { label: `Код ${state}`, color: "default" };

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return "-";
  const ms = new Date(finishedAt) - new Date(startedAt);
  if (Number.isNaN(ms) || ms < 0) return "-";

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, "0");
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

// Единый компонент раздела "Дозагрузка госзакупок"
export const BackFillData = () => {
  // ---------- состояние блока запуска дозагрузки ----------
  const [entity, setEntity] = useState(ENTITY_OPTIONS[0]);
  const [triggerDateFrom, setTriggerDateFrom] = useState("");
  const [triggerDateTo, setTriggerDateTo] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitResult, setSubmitResult] = useState(null);

  // id и последнее известное состояние процесса, запущенного из блока дозагрузки.
  // Пока процесс "выполняется" (state === 0) мы за ним следим и, как только он
  // меняет статус на "завершено" или "ошибка", обновляем текст в блоке дозагрузки.
  const trackedProcessIdRef = useRef(null);
  const trackedProcessStateRef = useRef(null);

  // ---------- состояние таблицы процессов ----------
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [integrationType, setIntegrationType] = useState("");
  const [state, setState] = useState("");

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const isValid = entity && triggerDateFrom && triggerDateTo;

  const handleDateFromChange = (value) => {
    setTriggerDateFrom(value);
    if (value && (!triggerDateTo || triggerDateTo < value)) {
      setTriggerDateTo(value);
    }
  };

  const handleDateToChange = (value) => {
    if (triggerDateFrom && value < triggerDateFrom) return;
    setTriggerDateTo(value);
  };

  // Проверяет, изменился ли статус отслеживаемого процесса, и если да —
  // обновляет текст результата в блоке дозагрузки
  const checkTrackedProcess = useCallback((loadedItems) => {
    const trackedId = trackedProcessIdRef.current;
    if (!trackedId) return;

    const found = loadedItems.find((it) => it.id === trackedId);
    if (!found) return;
    if (found.state === trackedProcessStateRef.current) return;

    trackedProcessStateRef.current = found.state;

    if (found.state === 1) {
      setSubmitResult("Процесс завершен");
      trackedProcessIdRef.current = null;
    } else if (found.state === 2) {
      setSubmitResult("Процесс завершился с ошибкой");
      trackedProcessIdRef.current = null;
    } else if (found.state === 3) {
      setSubmitResult("Процесс отменен");
      trackedProcessIdRef.current = null;
    }
    // state === 0 — процесс всё ещё выполняется, продолжаем следить
  }, []);

  const loadProcesses = useCallback(
    async ({ trackNewest = false } = {}) => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
        });

        if (dateFrom) params.set("dateFrom", toUtcIso(dateFrom));
        if (dateTo) params.set("dateTo", toUtcIso(dateTo));
        if (integrationType !== "")
          params.set("integrationType", integrationType);
        if (state !== "") params.set("state", state);

        const response = await fetchWithToken(
          `/api/dashboard/processes?${params.toString()}`,
          { method: "GET" },
        );

        if (!response.ok) {
          throw new Error(`Ошибка запроса: ${response.status}`);
        }

        const data = await response.json();
        const loadedItems = data.data ?? [];
        setItems(loadedItems);
        setTotalCount(data.all ?? 0);

        if (trackNewest) {
          // Только что запустили дозагрузку — определяем её процесс как самый
          // свежий среди тех, что сейчас "выполняется"
          const running = loadedItems
            .filter((it) => it.state === 0)
            .sort((a, b) => b.id - a.id);
          if (running[0]) {
            trackedProcessIdRef.current = running[0].id;
            trackedProcessStateRef.current = 0;
          }
        } else {
          checkTrackedProcess(loadedItems);
        }
      } catch (err) {
        setError(err.message || "Не удалось загрузить список процессов");
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      pageSize,
      dateFrom,
      dateTo,
      integrationType,
      state,
      checkTrackedProcess,
    ],
  );

  useEffect(() => {
    loadProcesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleSubmit = async (item = null) => {
    if (!isValid && !item) return;

    const isRetry = !!item;

    setSubmitLoading(true);
    setSubmitError("");
    setSubmitResult(null);
    trackedProcessIdRef.current = null;
    trackedProcessStateRef.current = null;

    // Для обычной дозагрузки начинаем отслеживать новый процесс
    if (!isRetry) {
      setTimeout(() => {
        loadProcesses({ trackNewest: true });
      }, 1000);
    }

    try {
      let body = {
        entity,
        dateFrom: toUtcIso(triggerDateFrom),
        dateTo: toUtcIso(triggerDateTo),
      };

      if (item) {
        body = {
          entity: item.entity,
          dateFrom: item.dateFrom,
          dateTo: item.dateTo,
        };
      }

      const response = await fetchWithToken(
        `/api/dashboard/backfill-big-data`,
        {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify(body),
        },
      );

      const text = await response.text();

      const res = text
        .replace(
          /Дата запуска \(UTC\): (\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})/,
          (_, day, month, year, hour, minute, second) => {
            const utcDate = new Date(
              Date.UTC(
                Number(year),
                Number(month) - 1,
                Number(day),
                Number(hour),
                Number(minute),
                Number(second),
              ),
            );

            const local = utcDate.toLocaleString("ru-RU", {
              timeZone: "Asia/Almaty",
            });

            return `Дата запуска: ${local}`;
          },
        )
        .replaceAll("\n", "")
        .replaceAll('"', "");

      if (!response.ok) {
        throw new Error(`Ошибка запроса: ${response.status}`);
      }

      // После повторной дозагрузки просто обновляем таблицу,
      // после обычной продолжаем отслеживать новый процесс.
      loadProcesses({ trackNewest: !isRetry });

      setSubmitResult(res);
    } catch (err) {
      setSubmitError(err.message || "Не удалось выполнить дозагрузку");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    loadProcesses();
  };

  const clearFilters = () => {
    setIntegrationType("");
    setState("");
    setDateFrom("");
    setDateTo("");
    setPageSize(20);
    loadProcesses();
  };

  const goFirst = () => setPage(1);
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goLast = () => setPage(totalPages);

  const stopProcess = async (processId) => {
    try {
      await fetchWithToken(
        `/api/dashboard/backfill-big-data/${processId}/cancel`,
        { method: "POST" },
      );
      loadProcesses();
    } catch (err) {}
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Дозагрузка госзакупок
      </Typography>

      {/* ---------- Блок запуска дозагрузки ---------- */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Запуск дозагрузки
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs="auto">
            <TextField
              select
              size="small"
              label="Сущность"
              sx={{ width: 200 }}
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
            >
              {ENTITY_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs="auto">
            <TextField
              size="small"
              type="datetime-local"
              label="Дата с"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 210 }}
              value={triggerDateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
            />
          </Grid>

          <Grid item xs="auto">
            <TextField
              size="small"
              type="datetime-local"
              label="Дата по"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 210 }}
              inputProps={{ min: triggerDateFrom || undefined }}
              value={triggerDateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          disabled={!isValid || submitLoading}
          onClick={() => handleSubmit()} // было: onClick={handleSubmit}
          startIcon={
            submitLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {submitLoading ? "Выполняется..." : "Запустить дозагрузку"}
        </Button>

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}

        {submitResult && (
          <Alert severity="success" sx={{ mt: 2 }} icon={false}>
            {submitResult}
          </Alert>
        )}
      </Paper>

      {/* ---------- Блок списка процессов ---------- */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Процессы дозагрузки
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs="auto">
            <TextField
              size="small"
              type="datetime-local"
              label="Дата с"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 210 }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs="auto">
            <TextField
              size="small"
              type="datetime-local"
              label="Дата по"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 210 }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Тип интеграции"
              placeholder="код"
              value={integrationType}
              onChange={(e) => setIntegrationType(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Статус"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value={0}>В процессе</MenuItem>
              <MenuItem value={1}>Завершено</MenuItem>
              <MenuItem value={2}>Ошибка</MenuItem>

              <MenuItem value={3}>Отменен</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="На странице"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={6}
            sm={5}
            sx={{ display: "flex", alignItems: "center", gap: "50px" }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              Применить
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={clearFilters}
              disabled={loading}
            >
              Очистить
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Тип интеграции</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Начало</TableCell>
                <TableCell>Окончание</TableCell>
                <TableCell>Длительность</TableCell>
                <TableCell align="right">Новых</TableCell>
                <TableCell align="right">Обновлено</TableCell>
                <TableCell>Период (с - по)</TableCell>
                <TableCell>Страница</TableCell>
                <TableCell>Отмена процесса</TableCell>
                <TableCell>Повторная дозагрузка</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    align="center"
                    sx={{ color: "text.secondary", py: 3 }}
                  >
                    Ничего не найдено
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                items.map((item) => {
                  const stateMeta = getStateMeta(item.state);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.integrationType}</TableCell>
                      <TableCell>
                        <Chip
                          label={stateMeta.label}
                          color={stateMeta.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {item.startedAt
                          ? new Date(item.startedAt).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {item.finishedAt
                          ? new Date(item.finishedAt).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {formatDuration(item.startedAt, item.finishedAt)}
                      </TableCell>
                      <TableCell align="right">{item.insertedNew}</TableCell>
                      <TableCell align="right">{item.updated}</TableCell>
                      <TableCell>
                        {item.dateFrom
                          ? new Date(item.dateFrom).toLocaleString()
                          : "-"}
                        {" — "}
                        {item.dateTo
                          ? new Date(item.dateTo).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {item.state === 1
                          ? item.totalPages
                          : `${item.currentPage}/${item.totalPages}`}
                      </TableCell>
                      <TableCell>
                        {item.state === 0 ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => stopProcess(item.id)}
                          >
                            Остановить
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {item.state === 2 ? (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleSubmit(item)}
                          >
                            Повторить
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Всего: {totalCount} | Страница {page} из {totalPages}
          </Typography>

          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={goFirst}
              disabled={page <= 1 || loading}
              title="В начало"
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={goPrev}
              disabled={page <= 1 || loading}
              title="Назад"
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={goNext}
              disabled={page >= totalPages || loading}
              title="Вперёд"
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={goLast}
              disabled={page >= totalPages || loading}
              title="В конец"
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};
